import { useConnectMicrosoftCalendarMutation } from '@/api/piloteer';
import { Spinner } from '@/ui/@components/feedback/loading/spinner';
import { Button } from '@/ui/@components/primitives/button';
import { APP_ROUTES } from '@/ui/application-routes';
import { useToast } from '@/ui/hooks/use-toast';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Module-level flag to prevent duplicate requests in StrictMode
let isProcessing = false;

const MicrosoftCalendarOAuthCallback: FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isConnecting = useRef(false);
  const mounted = useRef(true);
  const [error, setError] = useState<string | null>(null);
  const [isTimeout, setIsTimeout] = useState(false);

  const [connectMicrosoftCalendar] = useConnectMicrosoftCalendarMutation();

  const handleConnectMicrosoftCalendar = useCallback(
    async (authorizationCode: string) => {
      // Prevent duplicate requests using module-level flag
      if (isProcessing || isConnecting.current) {
        console.log('Skipping duplicate request');
        return;
      }
      isProcessing = true;
      isConnecting.current = true;

      // Set a timeout to handle stuck states
      const timeoutId = setTimeout(() => {
        if (mounted.current && isConnecting.current) {
          console.error('Connection timeout after 30 seconds');
          setIsTimeout(true);
          setError('Connection timeout. Please try again.');
        }
      }, 30000); // 30 second timeout

      try {
        await connectMicrosoftCalendar({
          authorizationCode,
        }).unwrap();

        clearTimeout(timeoutId);

        console.log('Microsoft Calendar connection successful');

        toast({
          title: 'Microsoft Calendar connected',
          description: 'Now you can invite Piloteer to your scheduled meetings',
          variant: 'success',
        });

        // Always navigate on success, regardless of mounted state
        setTimeout(() => {
          console.log('Navigating to integrations page...');
          navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
            state: { microsoftCalendarConnected: true },
            replace: true,
          });
        }, 500);
      } catch (error) {
        clearTimeout(timeoutId);

        const err = error as {
          status?: number;
          data?: { message?: string; error?: string };
          message?: string;
        };

        console.error('Failed to connect Microsoft Calendar:', {
          status: err.status,
          data: err.data,
          message: err.message,
          timestamp: new Date().toISOString(),
        });

        const errorMessage =
          err.data?.message ||
          err.data?.error ||
          err.message ||
          'Unknown error occurred';
        setError(errorMessage);

        // Handle specific error cases
        let toastTitle = 'Failed to connect Microsoft Calendar';
        let toastDescription = errorMessage;

        if (err.status === 400) {
          toastTitle = 'Invalid or expired authorization';
          toastDescription =
            'The authorization code may have expired or already been used. Please try connecting again.';
        }

        toast({
          title: toastTitle,
          description: toastDescription,
          variant: 'destructive',
        });

        // Always navigate on error, regardless of mounted state
        setTimeout(() => {
          navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
            state: {
              microsoftCalendarConnectError: true,
              errorDetails: errorMessage,
            },
            replace: true,
          });
        }, 1000);
      } finally {
        isConnecting.current = false;
        // Reset the module-level flag after a delay to allow retries
        setTimeout(() => {
          isProcessing = false;
        }, 3000);
      }
    },
    [connectMicrosoftCalendar, navigate, toast]
  );

  useEffect(() => {
    const authorizationCode = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Clear URL parameters to prevent reuse
    if (authorizationCode || error) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check for OAuth errors
    if (error) {
      console.error('OAuth error received:', { error, errorDescription });
      setError(errorDescription || error);

      toast({
        title: 'Authorization failed',
        description: errorDescription || error,
        variant: 'destructive',
      });

      setTimeout(() => {
        navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
          state: {
            microsoftCalendarConnectError: true,
            errorDetails: errorDescription || error,
          },
          replace: true,
        });
      }, 2000);
      return;
    }

    if (authorizationCode) {
      handleConnectMicrosoftCalendar(authorizationCode);
    } else {
      console.warn('No authorization code found in URL');
      setError('No authorization code received');

      setTimeout(() => {
        navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
          state: {
            microsoftCalendarConnectError: true,
            errorDetails: 'No authorization code received',
          },
          replace: true,
        });
      }, 2000);
    }

    // Cleanup function to prevent memory leaks
    return () => {
      console.log('MicrosoftCalendarOAuthCallback unmounting');
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to ensure this only runs once

  const handleRetry = () => {
    // Redirect back to integrations to retry
    navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, { replace: true });
  };

  if (error || isTimeout) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-center max-w-md'>
          <div className='mb-4'>
            <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10'>
              <svg
                className='w-8 h-8 text-destructive'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
          </div>
          <h2 className='text-2xl font-semibold mb-2'>
            {isTimeout ? 'Connection Timeout' : 'Connection Failed'}
          </h2>
          <p className='text-muted-foreground mb-6'>
            {error || 'The connection took too long. Please try again.'}
          </p>
          <Button onClick={handleRetry}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='text-center'>
        <Spinner />
        <p className='mt-4 text-xl font-medium'>
          Connecting your Microsoft Calendar
        </p>
        <p className='mt-2 text-muted-foreground'>
          Please wait while we complete the setup...
        </p>
      </div>
    </div>
  );
};

export default MicrosoftCalendarOAuthCallback;
